import { useState } from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { Button } from './Button';

export interface PaginationProps {
  /** Current page number (1-indexed) */
  page: number;
  /** Total number of items */
  total: number;
  /** Items per page */
  limit: number;
  /** Callback when page changes */
  onPageChange: (page: number) => void;
  /** Optional className for styling */
  className?: string;
}

/**
 * Reusable pagination component
 *
 * Features:
 * - Shows current range (e.g., "1-25 of 100")
 * - First/Last page buttons
 * - Previous/Next buttons
 * - Page number input to jump to specific page
 * - Fully keyboard accessible
 *
 * @example
 * ```tsx
 * <Pagination
 *   page={currentPage}
 *   total={totalItems}
 *   limit={25}
 *   onPageChange={setCurrentPage}
 * />
 * ```
 */
export function Pagination({ page, total, limit, onPageChange, className = '' }: PaginationProps) {
  const [inputValue, setInputValue] = useState(page.toString());

  // Calculate pagination values
  const totalPages = Math.ceil(total / limit);
  const startItem = total === 0 ? 0 : (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, total);

  // Handlers
  const goToPage = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== page) {
      onPageChange(newPage);
      setInputValue(newPage.toString());
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const pageNum = parseInt(inputValue, 10);
      if (!isNaN(pageNum)) {
        goToPage(pageNum);
      } else {
        setInputValue(page.toString());
      }
    } else if (e.key === 'Escape') {
      setInputValue(page.toString());
      e.currentTarget.blur();
    }
  };

  const handleInputBlur = () => {
    const pageNum = parseInt(inputValue, 10);
    if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= totalPages) {
      goToPage(pageNum);
    } else {
      setInputValue(page.toString());
    }
  };

  // Update input when page prop changes externally
  if (page.toString() !== inputValue && parseInt(inputValue, 10) !== page) {
    setInputValue(page.toString());
  }

  // If no items, show minimal UI
  if (total === 0) {
    return (
      <div className={`flex items-center justify-between py-3 ${className}`}>
        <div className="text-sm text-foreground-muted">
          No items to display
        </div>
      </div>
    );
  }

  // If only one page, don't show pagination controls
  if (totalPages <= 1) {
    return (
      <div className={`flex items-center justify-between py-3 ${className}`}>
        <div className="text-sm text-foreground-muted">
          Showing <span className="font-medium text-foreground">{startItem}</span> to{' '}
          <span className="font-medium text-foreground">{endItem}</span> of{' '}
          <span className="font-medium text-foreground">{total}</span> results
        </div>
      </div>
    );
  }

  return (
    <div className={`flex items-center justify-between py-3 ${className}`}>
      {/* Left: Item range */}
      <div className="text-sm text-foreground-muted">
        Showing <span className="font-medium text-foreground">{startItem}</span> to{' '}
        <span className="font-medium text-foreground">{endItem}</span> of{' '}
        <span className="font-medium text-foreground">{total}</span> results
      </div>

      {/* Right: Navigation controls */}
      <div className="flex items-center gap-2">
        {/* First page */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => goToPage(1)}
          disabled={page === 1}
          title="First page"
          aria-label="Go to first page"
        >
          <ChevronsLeft className="h-4 w-4" />
        </Button>

        {/* Previous page */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => goToPage(page - 1)}
          disabled={page === 1}
          title="Previous page"
          aria-label="Go to previous page"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        {/* Page input */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-foreground-muted">Page</span>
          <input
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            onBlur={handleInputBlur}
            className="w-12 rounded border border-border bg-background px-2 py-1 text-center text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            aria-label="Current page"
          />
          <span className="text-sm text-foreground-muted">of {totalPages}</span>
        </div>

        {/* Next page */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => goToPage(page + 1)}
          disabled={page === totalPages}
          title="Next page"
          aria-label="Go to next page"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>

        {/* Last page */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => goToPage(totalPages)}
          disabled={page === totalPages}
          title="Last page"
          aria-label="Go to last page"
        >
          <ChevronsRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
